import jwtDecode, { JwtPayload } from 'jwt-decode';

import type { Nullable } from '@altirllc/shared/utils/types/common';

import { refreshToken } from '@/services/auth';

import {
  RECENT_REFRESHES_THRESHOLD_MS,
  MAX_SUCCESSFUL_REFRESHES_DURING_INTERVAL,
} from '@/config/refreshTokenRateLimiter';
import { handleError } from '@/utils/errorUtils';

import { tokenUtils } from '@/tokenUtils';

import { logout } from './authUtils';

export const tryRefreshAndPersistToken = async () => {
  try {
    const { data } = await refreshToken();

    tokenUtils.setTokens(data);

    return data.access_token;
  } catch (error) {
    handleError(error, 'REFRESH_TOKEN');
    logout();
  }

  return null;
};

export const getValidAccessToken = () => {
  const token = tokenUtils.getLocallyStoredToken();
  const decodedToken: JwtPayload = jwtDecode(token);
  const tokenExpirationTime = (decodedToken.exp || 0) * 1000;
  const expiresInMinutes = (tokenExpirationTime - Date.now()) / 1000 / 60;

  if (expiresInMinutes <= 1) {
    return tryRefreshAndPersistToken();
  }

  return token;
};

/*
 Creates a limiter which prevents endless loop in the next case.
 This is unlikely to happen, but it's better to log out in such a case:
 - Client makes a request
 - API responds with 401
 - Client refreshes access token
 - API responds with 200 but with invalid access token
 - Client retries failed request
 - API responds with 401
 - Client refreshes token
 etc.
 */
const createFaultyRefreshRateLimiter = () => {
  let lastSuccessfulRefreshes: number[] = [];
  const removeIrrelevantTimestamps = () => {
    const now = Date.now();
    const lowerEdge = now - RECENT_REFRESHES_THRESHOLD_MS;

    lastSuccessfulRefreshes = lastSuccessfulRefreshes.filter((timestamp) => timestamp > lowerEdge);
  };

  return {
    onSuccess: () => {
      removeIrrelevantTimestamps();
      lastSuccessfulRefreshes.push(Date.now());
    },
    onFail: () => {
      lastSuccessfulRefreshes = [];
    },
    isLimitReached: () => {
      removeIrrelevantTimestamps();

      return lastSuccessfulRefreshes.length + 1 >= MAX_SUCCESSFUL_REFRESHES_DURING_INTERVAL;
    },
  };
};

/**
 * Create synchronized refreshToken function.
 * If several simultaneous requests fail with 401 response,
 * then refresh token once and make other failed request to wait for it to complete before retrying.
 */
export const createSynchronizedRefreshAccessToken = (refresh: () => Promise<unknown>) => {
  let refreshPromise: Nullable<Promise<unknown>> = null;
  const rateLimiter = createFaultyRefreshRateLimiter();

  return async () => {
    if (!refreshPromise) {
      if (rateLimiter.isLimitReached()) {
        throw new Error('Max refreshes count is limited. Aborting');
      }

      refreshPromise = refresh()
        .then(rateLimiter.onSuccess)
        .catch(rateLimiter.onFail)
        .finally(() => {
          refreshPromise = null;
        });
    }

    await refreshPromise;
  };
};
